<template>
    <div class="space-y-5">
        <div class="inline-flex items-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold">
            💬 Posts</div>
        <p class="text-gray-600 text-sm mt-2">Share updates, photos, or videos. Visibility controls who can see each post.</p>

        <div v-if="isOwnProfile" class="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-sm">
            <div class="flex items-start gap-3">
                <div class="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-lg text-indigo-700">
                    🗣️
                </div>
                <div class="flex-1">
                    <textarea v-model.trim="composerText" rows="3" class="w-full border border-gray-200 rounded-xl p-3 bg-white outline-none focus:border-indigo-500"
                        placeholder="What's on your mind?"></textarea>

                    <div class="mt-3 flex flex-wrap items-center gap-2">
                        <select v-model="visibility" class="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                            <option value="PUBLIC">Public</option>
                            <option value="FRIENDS">Friends</option>
                            <option value="ONLY_ME">Only Me</option>
                        </select>

                        <label class="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                            <span>📷</span>
                            <span>Photo/Video</span>
                            <input type="file" class="hidden" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime,video/ogg"
                                multiple @change="onMediaChange">
                        </label>

                        <div v-if="selectedMediaCount" class="text-sm text-gray-600">
                            {{ selectedMediaCount }} file(s) selected
                            <button class="ml-2 text-indigo-600 hover:text-indigo-800" @click="clearMedia" type="button">
                                Clear
                            </button>
                        </div>

                        <div class="ml-auto">
                            <button :disabled="isPosting" @click="submitPost"
                                class="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed">
                                {{ isPosting ? 'Posting...' : 'Post' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isLoading" class="text-center text-gray-500 py-10">Loading posts...</div>
        <div v-else>
            <div v-if="posts.length === 0" class="text-center text-gray-500 py-10">
                No posts yet.
            </div>

            <div v-for="p in posts" :key="p.id" class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm mb-4">
                <div class="flex items-start justify-between gap-3">
                    <div class="flex items-start gap-3 min-w-0">
                        <img :src="getProfilePicture(p.user && p.user.profile_picture_url ? p.user.profile_picture_url : '')"
                            :alt="(p.user && p.user.name) ? p.user.name : 'avatar'"
                            class="w-10 h-10 rounded-xl object-cover bg-gray-100 border border-gray-100" />
                        <div class="min-w-0">
                            <div class="font-semibold text-gray-900 truncate">{{ p.user && p.user.name ? p.user.name : '—' }}</div>
                            <div class="text-xs text-gray-500 flex items-center gap-2">
                                <span>{{ formatDate(p.created_at) }}</span>
                                <span>•</span>
                                <span class="inline-flex items-center" :title="formatVisibility(p.visibility)">
                                    <font-awesome-icon v-if="p.visibility === 'ONLY_ME'" icon="lock" />
                                    <font-awesome-icon v-else-if="p.visibility === 'FRIENDS'" icon="users" />
                                    <font-awesome-icon v-else icon="globe" />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="p.content" class="mt-3 text-gray-800 whitespace-pre-line">
                    {{ p.content }}
                </div>

                <div v-if="p.media && p.media.length" class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div v-for="m in p.media" :key="m.id" class="rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                        <img v-if="m.media_type === 'IMAGE'" :src="resolveMediaUrl(m.url)" class="w-full h-auto block" alt="post media" />
                        <video v-else-if="m.media_type === 'VIDEO'" :src="resolveMediaUrl(m.url)" class="w-full h-auto block" controls></video>
                    </div>
                </div>
            </div>
            <div v-if="isLoadingMore" class="text-center text-gray-500 py-4">
                Loading more...
            </div>
            <div v-else-if="posts.length > 0 && pageInfo && pageInfo.hasMorePages === false" class="text-center text-gray-400 py-4 text-sm">
                No more posts
            </div>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import { fileSecurityValidator } from '../../../helpers/fileSecurity';

const LIST_USER_THOUGHT_POSTS = gql`
  query ListUserThoughtPosts($userToken: String, $first: Int, $page: Int) {
    listUserThoughtPosts(userToken: $userToken, first: $first, page: $page) {
      error
      error_code
      message
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
      data {
        id
        content
        visibility
        created_at
        user { id name profile_picture_url }
        media { id media_type url original_filename }
      }
    }
  }
`;

const CREATE_THOUGHT_POST = gql`
  mutation CreateThoughtPost($input: CreateThoughtPostInput!) {
    createThoughtPost(input: $input) {
      error
      error_code
      message
      data {
        id
      }
    }
  }
`;

export default {
    name: 'profile-posts',
    props: {
        userToken: { type: String, default: '' },
        isOwnProfile: { type: Boolean, default: false },
        postsRefreshKey: { type: Number, default: 0 },
    },
    watch: {
        postsRefreshKey: function () {
            // Follow/unfollow changes visibility rules without changing route/props.
            this.resetPostsState();
            this.fetchPosts(1, true);
        },
        userToken: function () {
            // Parent route can change while this component instance is reused.
            this.resetPostsState();
            this.fetchPosts(1, true);
        },
        isOwnProfile: function () {
            // Own vs other profile changes how composer renders; refresh posts too.
            this.resetPostsState();
            this.fetchPosts(1, true);
        },
    },
    data() {
        return {
            composerText: '',
            visibility: 'PUBLIC',
            mediaFiles: [],
            posts: [],
            pageInfo: { currentPage: 1, hasMorePages: false },
            isLoading: true,
            isLoadingMore: false,
            isPosting: false,
            postsScrollTarget: null,
            postsScrollHandler: null,
        };
    },
    computed: {
        selectedMediaCount() {
            return this.mediaFiles && this.mediaFiles.length ? this.mediaFiles.length : 0;
        }
    },
    mounted() {
        this.fetchPosts(1, true);
        this.bindPostsScrollListener();
    },
    beforeUnmount() {
        this.unbindPostsScrollListener();
    },
    methods: {
        resetPostsState() {
            this.posts = [];
            this.pageInfo = { currentPage: 1, hasMorePages: false };
            this.isLoadingMore = false;
            this.isPosting = false;
            this.clearMedia();
        },
        formatVisibility(v) {
            if (v === 'ONLY_ME') return 'Only me';
            if (v === 'FRIENDS') return 'Friends';
            return 'Public';
        },
        formatDate(dt) {
            if (!dt) return '';
            try {
                return new Date(dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
            } catch (e) {
                return dt;
            }
        },
        getProfilePicture(url) {
            if (!url) return this.getDefaultAvatar();
            if (url.indexOf('http') === 0) return url;
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        getDefaultAvatar() {
            return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNlNWU3ZWYiLz4KPHBhdGggZD0iTTIwIDEwQzIyLjA3NjEgMTAgMjQgMTIuMDIzOSAyNCAxNEMyNCAxNS45NzYxIDIyLjA3NjEgMTggMjAgMThDMTcuOTIzOSAxOCAxNiAxNS45NzYxIDE2IDE0QzE2IDEyLjAyMzkgMTcuOTIzOSAxMCAyMCAxMFoiIGZpbGw9IiM5Y2EzYWYiLz4KPHBhdGggZD0iTTI4IDI4QzI4IDI0LjY4NjMgMjQuNDE4MyAyMiAyMCAyMkMxNS41ODE3IDIyIDEyIDI0LjY4NjMgMTIgMjhIMjhaIiBmaWxsPSIjOWNhM2FmIi8+Cjwvc3ZnPgo=';
        },
        resolveMediaUrl(url) {
            if (!url) return '';
            if (url.indexOf('http') === 0) return url;
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        bindPostsScrollListener() {
            this.unbindPostsScrollListener();
            const el = document.getElementById('dashboard-scroll-container');
            if (el) {
                this.postsScrollTarget = el;
                this.postsScrollHandler = this.handlePostsScroll.bind(this);
                this.postsScrollTarget.addEventListener('scroll', this.postsScrollHandler, { passive: true });
                return;
            }
            // Fallback: window scroll (in case profile is not inside dashboard-scroll-container)
            this.postsScrollTarget = window;
            this.postsScrollHandler = this.handlePostsScroll.bind(this);
            window.addEventListener('scroll', this.postsScrollHandler, { passive: true });
        },
        unbindPostsScrollListener() {
            if (this.postsScrollHandler && this.postsScrollTarget) {
                if (this.postsScrollTarget === window) {
                    window.removeEventListener('scroll', this.postsScrollHandler);
                } else if (this.postsScrollTarget.removeEventListener) {
                    this.postsScrollTarget.removeEventListener('scroll', this.postsScrollHandler);
                }
            }
            this.postsScrollHandler = null;
            this.postsScrollTarget = null;
        },
        handlePostsScroll() {
            if (this.isLoading || this.isLoadingMore) {
                return;
            }
            const info = this.pageInfo;
            if (!info || !info.hasMorePages) {
                return;
            }
            if (!this.posts || this.posts.length === 0) {
                return;
            }

            const root = this.postsScrollTarget;
            if (!root) {
                return;
            }

            let bottomGap = 999999;
            if (root === window) {
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
                const clientHeight = window.innerHeight || document.documentElement.clientHeight || 0;
                const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
                bottomGap = scrollHeight - scrollTop - clientHeight;
            } else {
                bottomGap = root.scrollHeight - root.scrollTop - root.clientHeight;
            }

            if (bottomGap >= 200) {
                return;
            }
            this.loadMore();
        },
        clearMedia() {
            this.mediaFiles = [];
        },
        async onMediaChange(e) {
            var files = e && e.target && e.target.files ? e.target.files : null;
            if (!files || !files.length) return;

            var picked = [];
            for (var i = 0; i < files.length; i++) {
                picked.push(files[i]);
            }

            // Validate each file (image/video) - max 25MB each
            for (var j = 0; j < picked.length; j++) {
                var f = picked[j];
                try {
                    var validation = await fileSecurityValidator.validateFileWithSize(f, 'images_and_videos', 25);
                    if (!validation.valid) {
                        this.$swal && this.$swal.fire({
                            icon: 'error',
                            title: 'Invalid file',
                            text: validation.message || 'Please choose a valid image/video.',
                        });
                        e.target.value = '';
                        return;
                    }
                } catch (err) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Validation Error', text: 'Unable to validate file. Please try again.' });
                    e.target.value = '';
                    return;
                }
            }

            this.mediaFiles = picked;
            e.target.value = '';
        },
        fetchPosts(page, replace) {
            var first = 10;
            this.isLoading = replace ? true : this.isLoading;
            this.isLoadingMore = !replace ? true : false;
            this.$apollo.query({
                query: LIST_USER_THOUGHT_POSTS,
                variables: { userToken: this.userToken || null, first: first, page: page },
                fetchPolicy: 'network-only',
            }).then((res) => {
                var payload = res && res.data && res.data.listUserThoughtPosts;
                if (!payload || payload.error) {
                    return;
                }

                var items = payload.data || [];
                if (replace) {
                    this.posts = items;
                } else {
                    this.posts = this.posts.concat(items);
                }
                this.pageInfo = payload.paginatorInfo || { currentPage: page, hasMorePages: false };
            }).finally(() => {
                this.isLoading = false;
                this.isLoadingMore = false;
            });
        },
        loadMore() {
            var next = (this.pageInfo && this.pageInfo.currentPage ? this.pageInfo.currentPage : 1) + 1;
            this.fetchPosts(next, false);
        },
        submitPost() {
            if (this.isPosting) return;

            var content = this.composerText ? String(this.composerText).trim() : '';
            var hasText = !!content;
            var hasMedia = this.mediaFiles && this.mediaFiles.length;
            if (!hasText && !hasMedia) {
                this.$swal && this.$swal.fire({ icon: 'info', title: 'Empty post', text: 'Write something or add a photo/video.' });
                return;
            }

            this.isPosting = true;
            var input = {
                content: hasText ? content : null,
                visibility: this.visibility || 'PUBLIC',
                media: hasMedia ? this.mediaFiles : null,
            };

            this.$apollo.mutate({
                mutation: CREATE_THOUGHT_POST,
                variables: { input: input },
            }).then((res) => {
                var payload = res && res.data && res.data.createThoughtPost;
                if (!payload || payload.error) {
                    this.$swal && this.$swal.fire({ icon: 'error', title: 'Post failed', text: (payload && payload.message) || 'Please try again.' });
                    return;
                }

                this.composerText = '';
                this.visibility = 'PUBLIC';
                this.clearMedia();
                this.fetchPosts(1, true);
            }).catch(() => {
                this.$swal && this.$swal.fire({ icon: 'error', title: 'Post failed', text: 'Please try again.' });
            }).finally(() => {
                this.isPosting = false;
            });
        }
    }
};
</script>

